import React, { useEffect, useMemo, useState } from 'react';

const BCG_COLORS = {
  Stars: { bg: '#fef3c7', text: '#92400e', border: '#f59e0b' },
  'Question Marks': { bg: '#dbeafe', text: '#1e40af', border: '#3b82f6' },
  'Cash Cows': { bg: '#d1fae5', text: '#065f46', border: '#10b981' },
  Dogs: { bg: '#fee2e2', text: '#991b1b', border: '#ef4444' },
};

export default function MerchantCenterCustomLabels() {
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');
  const [merchantId, setMerchantId] = useState('');
  const [activeLabelIndex, setActiveLabelIndex] = useState(0);
  const [summary, setSummary] = useState([]);
  const [products, setProducts] = useState([]);
  const [bcgStats, setBcgStats] = useState([]);
  const [bcgPeriod, setBcgPeriod] = useState(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedLabelValue, setSelectedLabelValue] = useState(null); // filter by label value
  const [pageIndex, setPageIndex] = useState(1);
  const PAGE_SIZE = 20;

  const brandId = useMemo(() => {
    try {
      const current = localStorage.getItem('currentBrandId') || sessionStorage.getItem('currentBrandId');
      if (current) return current;
    } catch (_) {}
    const tryKeys = ['brandId', 'selectedBrandId', 'activeBrandId', 'brand_id'];
    for (const k of tryKeys) {
      try { const v = localStorage.getItem(k) || sessionStorage.getItem(k); if (v) return v; } catch (_) {}
    }
    const tryJson = ['selectedBrand', 'currentBrand', 'activeBrand'];
    for (const k of tryJson) {
      try {
        const raw = localStorage.getItem(k) || sessionStorage.getItem(k);
        if (raw) { const obj = JSON.parse(raw); if (obj?.id) return obj.id; }
      } catch (_) {}
    }
    return '';
  }, []);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Auto-detect merchantId from existing MC data
  useEffect(() => {
    if (!brandId) return;
    (async () => {
      try {
        const res = await fetch(`https://api.glowytics.com/api/google-ads/merchant-center/accounts`, {
          headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
          credentials: 'include'
        });
        if (res.ok) {
          const data = await res.json();
          const accounts = data?.accounts || data?.data || [];
          if (accounts.length > 0) {
            setMerchantId(String(accounts[0]?.id || accounts[0]?.merchantId || ''));
          }
        }
      } catch (_) {}
    })();
  }, [brandId]);

  // Fetch custom labels overview
  useEffect(() => {
    if (!brandId) return;
    setLoading(true);
    setErrorMsg('');
    setPageIndex(1);
    (async () => {
      try {
        const qs = new URLSearchParams({
          labelIndex: String(activeLabelIndex),
          limit: '5000',
          offset: '0',
          ...(merchantId && { merchantId }),
          ...(debouncedSearch && { q: debouncedSearch })
        });
        const res = await fetch(`https://api.glowytics.com/api/google-ads/merchant-center/custom-labels/overview?${qs}`, {
          headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
          credentials: 'include'
        });
        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          throw new Error(errData?.error || res.statusText);
        }
        const data = await res.json();
        setSummary(data.summary || []);
        setProducts(data.products || []);
        setBcgStats(data.bcgStats || []);
        setBcgPeriod(data.bcgPeriod || null);
      } catch (err) {
        setErrorMsg(err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [brandId, merchantId, activeLabelIndex, debouncedSearch]);

  // Filtered products
  const filteredProducts = useMemo(() => {
    if (selectedLabelValue === null) return products;
    const labelKey = `custom_label${activeLabelIndex}`;
    return products.filter(p => p[labelKey] === selectedLabelValue);
  }, [products, selectedLabelValue, activeLabelIndex]);

  // Paginated products
  const pagedProducts = useMemo(() => {
    const start = (pageIndex - 1) * PAGE_SIZE;
    return filteredProducts.slice(start, start + PAGE_SIZE);
  }, [filteredProducts, pageIndex]);

  const totalPages = Math.ceil(filteredProducts.length / PAGE_SIZE);

  // BCG stats for display
  const bcgStatsMap = useMemo(() => {
    const map = {};
    (bcgStats || []).forEach(s => { map[s.category] = Number(s.cnt); });
    return map;
  }, [bcgStats]);

  // Count BCG-matched products
  const bcgMatchedCount = useMemo(() => {
    return products.filter(p => p.bcg_category).length;
  }, [products]);

  const labelTabs = ['Custom Label 0', 'Custom Label 1', 'Custom Label 2', 'Custom Label 3', 'Custom Label 4'];

  return (
    <div style={{ padding: 24, maxWidth: 1400, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>
          Merchant Center Custom Labels
        </h2>
        <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: 14 }}>
          View products grouped by custom labels with BCG category overlay
        </p>
      </div>

      {/* BCG Stats Cards */}
      {bcgPeriod && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
          <div style={{
            padding: '10px 16px', borderRadius: 8, background: '#f3f4f6',
            border: '1px solid #e5e7eb', fontSize: 13
          }}>
            <span style={{ color: '#6b7280' }}>BCG Period: </span>
            <strong>{bcgPeriod.year}/{String(bcgPeriod.month).padStart(2, '0')}</strong>
          </div>
          {['Stars', 'Question Marks', 'Cash Cows', 'Dogs'].map(cat => (
            <div key={cat} style={{
              padding: '10px 16px', borderRadius: 8,
              background: BCG_COLORS[cat].bg,
              border: `1px solid ${BCG_COLORS[cat].border}`,
              fontSize: 13, color: BCG_COLORS[cat].text
            }}>
              <strong>{bcgStatsMap[cat] || 0}</strong> {cat}
            </div>
          ))}
          <div style={{
            padding: '10px 16px', borderRadius: 8, background: '#f0fdf4',
            border: '1px solid #86efac', fontSize: 13, color: '#166534'
          }}>
            <strong>{bcgMatchedCount}</strong> / {products.length} MC products matched to BCG
          </div>
        </div>
      )}

      {/* Label Tabs */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 16, borderBottom: '2px solid #e5e7eb' }}>
        {labelTabs.map((tab, idx) => (
          <button
            key={idx}
            onClick={() => { setActiveLabelIndex(idx); setSelectedLabelValue(null); }}
            style={{
              padding: '10px 20px', border: 'none', cursor: 'pointer',
              background: activeLabelIndex === idx ? '#fff' : 'transparent',
              borderBottom: activeLabelIndex === idx ? '2px solid #3b82f6' : '2px solid transparent',
              marginBottom: -2,
              fontWeight: activeLabelIndex === idx ? 600 : 400,
              color: activeLabelIndex === idx ? '#1d4ed8' : '#6b7280',
              fontSize: 14, transition: 'all 0.15s'
            }}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Search */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center' }}>
        <input
          type="text"
          placeholder="Search products by title, offer ID, or label value..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{
            flex: 1, padding: '8px 14px', borderRadius: 8,
            border: '1px solid #d1d5db', fontSize: 14, outline: 'none'
          }}
        />
        {selectedLabelValue !== null && (
          <button
            onClick={() => { setSelectedLabelValue(null); setPageIndex(1); }}
            style={{
              padding: '8px 14px', borderRadius: 8, border: '1px solid #d1d5db',
              background: '#fee2e2', color: '#991b1b', cursor: 'pointer', fontSize: 13
            }}
          >
            Clear filter: {selectedLabelValue}
          </button>
        )}
      </div>

      {/* Error */}
      {errorMsg && (
        <div style={{ padding: 12, background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 8, marginBottom: 16, color: '#991b1b', fontSize: 14 }}>
          {errorMsg}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>Loading...</div>
      )}

      {!loading && (
        <div style={{ display: 'flex', gap: 20 }}>
          {/* Left sidebar - Label value breakdown */}
          <div style={{ minWidth: 260, maxWidth: 300 }}>
            <h4 style={{ margin: '0 0 8px', fontSize: 14, color: '#374151' }}>
              Label Values ({summary.length})
            </h4>
            <div style={{
              border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden',
              maxHeight: 600, overflowY: 'auto'
            }}>
              {summary.map((s, i) => {
                const val = s.label_value;
                if (!val) return null;
                const isActive = selectedLabelValue === val;
                return (
                  <div
                    key={i}
                    onClick={() => { setSelectedLabelValue(isActive ? null : val); setPageIndex(1); }}
                    style={{
                      padding: '8px 12px', cursor: 'pointer',
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      background: isActive ? '#eff6ff' : i % 2 === 0 ? '#fff' : '#f9fafb',
                      borderBottom: '1px solid #f3f4f6',
                      borderLeft: isActive ? '3px solid #3b82f6' : '3px solid transparent'
                    }}
                  >
                    <span style={{
                      fontSize: 13, color: '#111827',
                      maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                    }}>
                      {val}
                    </span>
                    <span style={{
                      fontSize: 12, color: '#6b7280', background: '#f3f4f6',
                      padding: '2px 8px', borderRadius: 10, fontWeight: 500
                    }}>
                      {Number(s.product_count)}
                    </span>
                  </div>
                );
              })}
              {summary.length === 0 && (
                <div style={{ padding: 16, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
                  No label values found
                </div>
              )}
            </div>
          </div>

          {/* Right - Products table */}
          <div style={{ flex: 1, overflow: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <h4 style={{ margin: 0, fontSize: 14, color: '#374151' }}>
                Products ({filteredProducts.length})
              </h4>
              {totalPages > 1 && (
                <div style={{ display: 'flex', gap: 4, alignItems: 'center', fontSize: 13 }}>
                  <button
                    disabled={pageIndex <= 1}
                    onClick={() => setPageIndex(p => p - 1)}
                    style={{
                      padding: '4px 10px', borderRadius: 6, border: '1px solid #d1d5db',
                      background: '#fff', cursor: pageIndex <= 1 ? 'not-allowed' : 'pointer',
                      opacity: pageIndex <= 1 ? 0.5 : 1
                    }}
                  >Prev</button>
                  <span style={{ color: '#6b7280' }}>{pageIndex} / {totalPages}</span>
                  <button
                    disabled={pageIndex >= totalPages}
                    onClick={() => setPageIndex(p => p + 1)}
                    style={{
                      padding: '4px 10px', borderRadius: 6, border: '1px solid #d1d5db',
                      background: '#fff', cursor: pageIndex >= totalPages ? 'not-allowed' : 'pointer',
                      opacity: pageIndex >= totalPages ? 0.5 : 1
                    }}
                  >Next</button>
                </div>
              )}
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: '#374151' }}>Product</th>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: '#374151' }}>Offer ID</th>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: '#374151' }}>Label {activeLabelIndex}</th>
                  <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 600, color: '#374151' }}>BCG Category</th>
                  <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600, color: '#374151' }}>Price</th>
                  <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 600, color: '#374151' }}>Availability</th>
                </tr>
              </thead>
              <tbody>
                {pagedProducts.map((p, i) => {
                  const labelVal = p[`custom_label${activeLabelIndex}`];
                  const bcgCat = p.bcg_category;
                  const bcgColor = bcgCat ? BCG_COLORS[bcgCat] : null;
                  return (
                    <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '8px 10px', display: 'flex', alignItems: 'center', gap: 8 }}>
                        {p.image_link && (
                          <img
                            src={p.image_link}
                            alt=""
                            style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 4, border: '1px solid #e5e7eb' }}
                            onError={e => { e.target.style.display = 'none'; }}
                          />
                        )}
                        <span style={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {p.title || '-'}
                        </span>
                      </td>
                      <td style={{ padding: '8px 10px', color: '#6b7280', fontFamily: 'monospace', fontSize: 12 }}>
                        {p.offer_id || '-'}
                      </td>
                      <td style={{ padding: '8px 10px' }}>
                        {labelVal ? (
                          <span style={{
                            background: '#f3f4f6', padding: '2px 8px', borderRadius: 4,
                            fontSize: 12, color: '#374151'
                          }}>
                            {labelVal}
                          </span>
                        ) : (
                          <span style={{ color: '#d1d5db', fontSize: 12 }}>-</span>
                        )}
                      </td>
                      <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                        {bcgCat ? (
                          <span style={{
                            background: bcgColor.bg, color: bcgColor.text,
                            border: `1px solid ${bcgColor.border}`,
                            padding: '2px 10px', borderRadius: 12,
                            fontSize: 12, fontWeight: 500
                          }}>
                            {bcgCat}
                            {p.bcg_workhorse && ' (WH)'}
                          </span>
                        ) : (
                          <span style={{ color: '#d1d5db', fontSize: 12 }}>-</span>
                        )}
                      </td>
                      <td style={{ padding: '8px 10px', textAlign: 'right', fontFamily: 'monospace', fontSize: 12 }}>
                        {p.price_value != null ? (
                          <>
                            {p.sale_price_value != null && (
                              <span style={{ textDecoration: 'line-through', color: '#9ca3af', marginRight: 4 }}>
                                {Number(p.price_value).toFixed(2)}
                              </span>
                            )}
                            <span>{Number(p.sale_price_value ?? p.price_value).toFixed(2)} {p.price_currency || ''}</span>
                          </>
                        ) : '-'}
                      </td>
                      <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                        <span style={{
                          fontSize: 11, padding: '2px 8px', borderRadius: 4,
                          background: p.availability === 'in stock' ? '#d1fae5' : '#fee2e2',
                          color: p.availability === 'in stock' ? '#065f46' : '#991b1b'
                        }}>
                          {p.availability || '-'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
                {pagedProducts.length === 0 && (
                  <tr>
                    <td colSpan={6} style={{ padding: 32, textAlign: 'center', color: '#9ca3af' }}>
                      No products found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {/* Bottom pagination */}
            {totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: 4, marginTop: 16, fontSize: 13 }}>
                <button
                  disabled={pageIndex <= 1}
                  onClick={() => setPageIndex(p => p - 1)}
                  style={{
                    padding: '4px 10px', borderRadius: 6, border: '1px solid #d1d5db',
                    background: '#fff', cursor: pageIndex <= 1 ? 'not-allowed' : 'pointer',
                    opacity: pageIndex <= 1 ? 0.5 : 1
                  }}
                >Prev</button>
                <span style={{ color: '#6b7280', lineHeight: '28px' }}>{pageIndex} / {totalPages}</span>
                <button
                  disabled={pageIndex >= totalPages}
                  onClick={() => setPageIndex(p => p + 1)}
                  style={{
                    padding: '4px 10px', borderRadius: 6, border: '1px solid #d1d5db',
                    background: '#fff', cursor: pageIndex >= totalPages ? 'not-allowed' : 'pointer',
                    opacity: pageIndex >= totalPages ? 0.5 : 1
                  }}
                >Next</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
